import React, { useState } from 'react';
import { Quote, PlayCircle, HelpCircle } from 'lucide-react';
import { motion } from "framer-motion";

const FAQS = [
  { q: 'How do I enroll in a course?', a: 'Browse to Courses page, select a video or course, and click Enroll Now. Complete the payment and access is granted instantly.' },
  { q: 'What payment methods are accepted?', a: 'We accept UPI, net banking, credit/debit cards, and wallets via Razorpay. All transactions are encrypted and secure.' },
  { q: 'How long do I have access after enrolling?', a: 'Access duration varies by course — typically 30 to 90 days. The exact validity period is shown on each course card.' },
  { q: 'Can I get a refund?', a: 'We offer a 7-day refund policy if you have watched less than 20% of course content. Contact support to initiate a request.' },
  { q: 'How do I submit an investment query?', a: 'Navigate to Services, click View More on any investment product, and fill in query form at the bottom of the page.' },
];

const Testimonials = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const testimonials = [
    {
      title: 'Jagatheeswaran from chennai',
      videoSrc: '/testimonial-1.mp4',
    },
    {
      title: 'Tamizharasi from Udumalpet',
      videoSrc: '/testimonial-2.mp4',
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--color-bg)] via-[var(--color-bg-2)]">
      {/* Hero Section */}
      <section
        className="relative overflow-hidden text-white bg-auto bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/testimonial-bg.png')" }}
      >
        
        <div className="relative">
          <div className="relative max-w-6xl mx-auto px-4 py-24 sm:py-32 md:py-40 min-h-[420px] sm:min-h-[520px] flex items-center justify-center">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, x: -80 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 tracking-tight max-w-4xl mx-auto"
            >
              You're Never <span className="text-white">Trading Alone</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="text-lg sm:text-xl text-white/90 mb-8 max-w-3xl mx-auto"
            >
              Real experiences from real investors who have transformed their financial journey with Stone Bench Investments
            </motion.p>
           
          </div>
        </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'Happy Clients', value: '1000+' },
              { label: 'Experience', value: '10+' },
              { label: 'Years of Trust', value: '8+' },              
              { label: 'Community', value: 'Growing' }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 1.2 + (i * 0.1),
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                whileHover={{ y: -4 }}
                className="glass p-4 sm:p-5 rounded-2xl text-center card-hover-glow"
              >
                <p className="text-[clamp(1.3rem,3vw,1.8rem)] font-black text-[#d2691e] mb-0.5">{stat.value}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#032d18]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section >

      {/* Testimonials Grid */}
      < section className="py-16 bg-white" >
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-[var(--color-text)] mb-4">
              Success Stories From Our Community
            </h2>
            <p className="text-base text-[var(--color-muted)] mb-8 max-w-3xl mx-auto">
              Join thousands of successful investors who have achieved their financial goals with Stone Bench Investments
            </p>
          </div>

          <div className="grid grid-cols-1 items-start md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card self-start p-4 sm:p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-[rgba(95,156,76,0.12)] border border-[rgba(95,156,76,0.2)] flex items-center justify-center text-[var(--color-accent)]">
                    <PlayCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-black text-[var(--color-text)] leading-snug">{testimonial.title}</h3>
                    <p className="text-sm font-semibold text-[var(--color-accent)]">Video testimonial</p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-black h-[360px] sm:h-[420px] md:h-[440px] lg:h-[480px]">
                  <video
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                    playsInline
                  >
                    <source src={testimonial.videoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section >

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(95,156,76,0.1)] border border-[rgba(95,156,76,0.2)] text-[var(--color-accent)] text-xs font-bold mb-4">
              <HelpCircle className="w-3 h-3" /> FREQUENTLY ASKED QUESTIONS
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-[var(--color-text)] mb-4">
              Got Questions? We Have Answers
            </h2>
            <p className="text-base text-[var(--color-muted)] mb-8 max-w-3xl mx-auto">
              Find answers to common questions about our courses, payments, and services
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-3">
              {FAQS.map((f, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0 }} 
                  whileInView={{ opacity: 1 }} 
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card overflow-hidden"
                >
                  <button 
                    className="w-full flex items-center justify-between p-5 text-left" 
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-bold text-[var(--color-text)] pr-4">{f.q}</span>
                    <span className={`text-[var(--color-accent)] text-lg font-bold shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openFaq === i && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }} 
                      animate={{ height: 'auto', opacity: 1 }}
                      className="px-5 pb-5 text-sm text-[var(--color-muted)] leading-relaxed border-t border-[var(--glass-border)] pt-4"
                    >
                      {f.a}
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div >
  );
};

export default Testimonials;
